import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { 
  Search, 
  MessageCircle, 
  Mail, 
  Phone,
  HelpCircle,
  Shield,
  CreditCard,
  Clock,
  Globe,
  Zap,
  Copy,
  Check,
  X
} from 'lucide-react';
import Footer from '@/components/Footer';
import AIChatWidget from '@/components/AIChatWidget';

const HelpCenter = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleSendEmail = () => {
    // Just show the modal - let user choose how to contact
    setShowEmailModal(true);
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText('support@routeflowai.com');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = 'support@routeflowai.com';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const faqCategories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Zap,
      questions: [
        {
          q: 'How do I create an account?',
          a: 'Click "Get Started" on the homepage, fill in your email, full name, and create a password. You\'ll instantly be able to start transferring money worldwide!'
        },
        {
          q: 'What countries can I send money to?',
          a: 'We support 121 countries worldwide including the US, UK, EU countries, Mexico, Philippines, Nigeria, India, UAE, Canada, Australia, and many more. Check our Supported Countries page for the full list.'
        },
        {
          q: 'How long does a transfer take?',
          a: 'Most transfers complete in 15-45 minutes depending on the route chosen. Our AI finds the fastest blockchain path for your specific transfer. You can track the status in real-time from your dashboard.'
        }
      ]
    },
    {
      id: 'fees-payments',
      title: 'Fees & Payments',
      icon: CreditCard,
      questions: [
        {
          q: 'What are the fees?',
          a: 'Total fees include: RouteFlow AI service fee (0.48%) + Payment processing fee (varies by method and region). For example, Visa/Mastercard is 2.2% in EEA or 3.5% outside EEA. Visit our Fees & Pricing page for complete details.'
        },
        {
          q: 'What payment methods do you accept?',
          a: 'We accept 10 payment methods: Mastercard, Visa (credit/debit cards), Apple Pay, Google Pay, SEPA Bank Transfer, PIX, Revolut Pay, Open Banking, SPEI, and Bank Transfer.'
        },
        {
          q: 'What is the minimum/maximum transfer amount?',
          a: 'Daily limit: €15 - €10,000. Monthly limit: €15,000. No KYC required for payments under €700/day with Visa, Mastercard, or Revolut.'
        },
        {
          q: 'Do you have any hidden fees?',
          a: 'Absolutely not! All fees are shown at checkout before you confirm. RouteFlow AI charges 0.48%, and payment processing fees vary by method (1.5% - 4.8%). Visit our Fees & Pricing page for full transparency.'
        }
      ]
    },
    {
      id: 'security',
      title: 'Security & Safety',
      icon: Shield,
      questions: [
        {
          q: 'Is RouteFlow AI safe?',
          a: 'Yes! We use bank-level 256-bit encryption, secure payment processing through our licensed partner Guardarian, and blockchain technology for secure, transparent transfers.'
        },
        {
          q: 'How is my money protected?',
          a: 'Your transfers are processed through Guardarian, a licensed and regulated payment provider. All transactions are traceable on the blockchain for complete transparency.'
        },
        {
          q: 'How do you protect my personal information?',
          a: 'We follow strict GDPR and data protection standards. Your data is encrypted, never sold, and only used to process your transfers.'
        },
        {
          q: 'What if something goes wrong?',
          a: 'Our support team is available 24/7 via chat, email (support@routeflowai.com), or phone. All transactions are traceable, and we work to resolve any issues promptly.'
        }
      ]
    },
    {
      id: 'how-it-works',
      title: 'How It Works',
      icon: HelpCircle,
      questions: [
        {
          q: 'How does AI optimize my transfer?',
          a: 'Our AI analyzes multiple blockchain routes in real-time to find the fastest, cheapest path for your specific transfer. This ensures you get the best exchange rates and lowest fees.'
        },
        {
          q: 'What currencies can I send?',
          a: 'We support 44 fiat currencies including USD, EUR, GBP, CAD, AUD, CHF, AED, SAR, INR, NGN, MXN, PHP, and many more. Check our Fiat Currencies page for the complete list.'
        },
        {
          q: 'How does my recipient receive money?',
          a: 'Your recipient gets local currency directly to their bank account. They don\'t need any crypto knowledge - we handle all the blockchain routing behind the scenes!'
        },
        {
          q: 'Can I track my transfer?',
          a: 'Yes! Every transfer has a real-time status that you can track from your dashboard. You\'ll see updates at each stage of the process.'
        }
      ]
    },
    {
      id: 'recipients',
      title: 'Recipients & Accounts',
      icon: Globe,
      questions: [
        {
          q: 'How do I send money?',
          a: 'Simply log in, click "Send Money", enter the amount and destination currency, and follow the prompts. Our AI will find the best route automatically.'
        },
        {
          q: 'Can I send to any country?',
          a: 'We support transfers to 121 countries. Most major destinations are covered including Europe, North America, Asia, Africa, and Latin America.'
        },
        {
          q: 'What information do I need to send money?',
          a: 'You\'ll need to specify the amount, source currency, and destination currency. Our payment partner will guide you through the recipient details during checkout.'
        }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: Clock,
      questions: [
        {
          q: 'My transfer is taking longer than expected - what do I do?',
          a: 'Most transfers complete within 45 minutes. If it\'s been longer, check your transaction history for updates or contact support at support@routeflowai.com or call +1 (302) 520-2688.'
        },
        {
          q: 'I forgot my password. How do I reset it?',
          a: 'Click "Forgot Password" on the login page. We\'ll send you a reset link via email.'
        },
        {
          q: 'The website is not loading. What should I do?',
          a: 'Try clearing your browser cache or using a different browser. If the problem persists, contact our support team via email or phone.'
        },
        {
          q: 'Can I cancel a transfer?',
          a: 'Once a transfer is processing on the blockchain, it cannot be canceled. Please contact support immediately at support@routeflowai.com if there\'s an issue.'
        }
      ]
    }
  ];

  const filteredFaqs = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(faq => 
      searchQuery === '' || 
      faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
      {/* Navigation */}
      <nav className='bg-white border-b shadow-sm'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center h-16'>
            <div className='flex items-center space-x-3 cursor-pointer' onClick={() => navigate('/')}>
              <img 
                src='https://customer-assets.emergentagent.com/job_smart-money-route/artifacts/6teoyzj7_Generated%20Image%20November%2018%2C%202025%20-%203_06PM.png' 
                alt='RouteFlow AI' 
                className='h-10 w-auto'
              />
              <span className='text-xl font-bold gradient-text'>RouteFlow AI</span>
            </div>
            <Button onClick={() => navigate('/')}>
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className='bg-gradient-to-r from-emerald-500 to-teal-600 text-white py-16'>
        <div className='max-w-4xl mx-auto px-4 text-center'>
          <h1 className='text-4xl md:text-5xl font-bold mb-4'>How can we help you?</h1>
          <p className='text-xl text-emerald-100 mb-8'>Search our knowledge base or browse categories below</p>
          
          {/* Search */}
          <div className='max-w-2xl mx-auto'>
            <div className='relative'>
              <Search className='absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5' />
              <Input
                type='text'
                placeholder='Search for answers...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='pl-12 h-14 text-lg bg-white'
              />
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Content */}
      <div className='max-w-5xl mx-auto px-4 py-12'>
        {filteredFaqs.length > 0 ? (
          <div className='space-y-8'>
            {filteredFaqs.map((category) => (
              <Card key={category.id} className='border-0 shadow-lg'>
                <CardHeader>
                  <CardTitle className='flex items-center space-x-3'>
                    <div className='w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center'>
                      <category.icon className='w-5 h-5 text-white' />
                    </div>
                    <span>{category.title}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Accordion type='single' collapsible className='w-full'>
                    {category.questions.map((faq, index) => (
                      <AccordionItem key={index} value={`item-${index}`}>
                        <AccordionTrigger className='text-left hover:text-emerald-600'>
                          {faq.q}
                        </AccordionTrigger>
                        <AccordionContent className='text-gray-600'>
                          {faq.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className='border-0 shadow-lg'>
            <CardContent className='text-center py-12'>
              <HelpCircle className='w-16 h-16 mx-auto mb-4 text-gray-400' />
              <p className='text-lg text-gray-600 mb-4'>No results found for "{searchQuery}"</p>
              <Button variant='outline' onClick={() => setSearchQuery('')}>
                Clear Search
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Contact Support */}
        <Card className='border-0 shadow-lg mt-12 bg-gradient-to-br from-emerald-500 to-teal-600 text-white'>
          <CardHeader>
            <CardTitle>Still need help?</CardTitle>
            <CardDescription className='text-emerald-100'>Our support team is here 24/7</CardDescription>
          </CardHeader>
          <CardContent>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              <div className='bg-white/10 backdrop-blur rounded-lg p-4'>
                <MessageCircle className='w-8 h-8 mb-2' />
                <h3 className='font-semibold mb-1'>Live Chat</h3>
                <p className='text-sm text-emerald-100 mb-3'>Chat with our AI assistant</p>
                <Button variant='secondary' size='sm' onClick={() => setIsChatOpen(true)}>Start Chat</Button>
              </div>
              
              <div className='bg-white/10 backdrop-blur rounded-lg p-4'>
                <Mail className='w-8 h-8 mb-2' />
                <h3 className='font-semibold mb-1'>Email Us</h3>
                <p className='text-sm text-emerald-100 mb-3'>support@routeflowai.com</p>
                <Button 
                  variant='secondary' 
                  size='sm'
                  onClick={handleSendEmail}
                >
                  Send Email
                </Button>
              </div>
              
              <div className='bg-white/10 backdrop-blur rounded-lg p-4'>
                <Phone className='w-8 h-8 mb-2' />
                <h3 className='font-semibold mb-1'>Call Us</h3>
                <p className='text-sm text-emerald-100 mb-2'>
                  <a href='tel:+13025202688' className='hover:underline block'>📞 +1 (302) 520-2688</a>
                  <a href='tel:+96170411882' className='hover:underline block'>📱 +961 70 411 882</a>
                </p>
                <p className='text-xs text-emerald-200'>Mon-Fri 9AM-6PM EST</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Chat Widget */}
      <AIChatWidget isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md shadow-2xl">
            <CardHeader className="relative">
              <Button 
                variant="ghost" 
                size="icon" 
                className="absolute right-2 top-2"
                onClick={() => setShowEmailModal(false)}
              >
                <X className="w-4 h-4" />
              </Button>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-emerald-600" />
                Email Support
              </CardTitle>
              <CardDescription>
                Send us an email and we'll get back to you within 24 hours
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Email Address</p>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">support@routeflowai.com</span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={copyEmail}
                    className="gap-2"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 text-emerald-600" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                Click the green button below to open your email app. If it didn't open, please copy the email address above and send us your message manually.
              </p>
              <div className="flex gap-2">
                <Button 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => {
                    window.location.href = 'mailto:support@routeflowai.com?subject=Support%20Request%20-%20RouteFlow%20AI';
                  }}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Open Email Client
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setShowEmailModal(false)}
                >
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default HelpCenter;
